/* eslint-disable react/require-default-props */
import {
	createContext,
	forwardRef,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'

import Center from '@components/Center/Center'
import IconButton from '@components/IconButton/IconButton'
import { faSearch } from '@fortawesome/pro-solid-svg-icons'
import { Box, Stack, TextFieldProps, Typography, useTheme } from '@mui/material'
import { RichTreeView } from '@mui/x-tree-view'
import errorShake from '@utils/animations'
import { Controller, FieldError, useFormContext } from 'react-hook-form'

import { Tree } from '../types'
import CustomTreeItem from './components/CustomTreeItem'
import CategoryTreeInputDialog from './dialogs/CategoryTreeInputDialog'
import CustomerTreeInputDialog from './dialogs/CustomerTreeInputDialog'
import DistributorTreeInputDialog from './dialogs/DistributorTreeInputDialog'
import {
	categorySearchResultsToTrees,
	getAllIds,
	mergeTrees,
	removeNodeFromTree,
} from './RHFTreeInput.utils'
import RHFTreeInputProvider from './RHFTreeInputProvider'

type Props = Omit<TextFieldProps, 'error'> & {
	rightSlot?: React.ReactNode
	required?: boolean
	onChange?: (tree: Tree[]) => void
	error?: FieldError
	dialogComponent: React.ComponentType
}

// Context to provide dialog control functions to custom dialog components
type TreeInputCtx = {
	name: string
	open: boolean
	onClose: () => void
	onSave: (results: CategorySearchResult[]) => void
}

export const TreeInputContext = createContext<TreeInputCtx>(null)

export const useTreeInput = () => {
	const ctx = useContext(TreeInputContext)
	if (!ctx) {
		throw new Error('Dialog must be used inside RHFTreeInput.')
	}
	return ctx
}

/**
 * RHFTreeInput
 * A tree input component integrated with react-hook-form.
 *
 * @param {string} name - The form field name registered with RHF.
 * @param {string} label - The label for the input field.
 * @param {boolean} required - Whether the field is required.
 * @param {React.ReactNode} rightSlot - Optional element rendered on the right side.
 * @param {React.ComponentType<DialogProps>} dialogComponent - Component used to render the selection dialog.
 */
function RHFTreeInput({ name, required = false, ...props }: Props) {
	const { control } = useFormContext()

	return (
		<Controller
			control={control}
			name={name}
			rules={{ required }}
			render={({ field, fieldState }) => (
				<Input {...field} required={required} name={name} {...props} error={fieldState.error} />
			)}
		/>
	)
}

// --------------------------------------------------------
// Rendered Input
// --------------------------------------------------------
const Input = forwardRef<HTMLDivElement, Props>(
	(
		{
			disabled = false,
			onChange = null,
			error = null,
			value = [] as Tree[],
			required = false,
			rightSlot = null,
			label,
			name,
			dialogComponent: DialogComponent,
		}: Props,
		ref
	) => {
		const [tree, setTree] = useState<Tree[]>(value as Tree[])
		const [open, setOpen] = useState(false)
		const [expandedNodes, setExpandedNodes] = useState<any[]>(getAllIds(value as Tree[]))

		const theme = useTheme()

		const hasError = Boolean(error)

		const fieldRef = useRef<HTMLDivElement>(null)

		useEffect(() => {
			if (hasError && fieldRef.current) {
				fieldRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
			}
		}, [hasError])

		useEffect(() => {
			onChange(tree)
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [tree])

		const handleAddItems = useCallback(
			(items: CategorySearchResult[]) => {
				const treesToMerge: SearchResultNode[] = categorySearchResultsToTrees(items)
				const newTree: Tree[] = mergeTrees(tree, treesToMerge)
				const newExpandedNodes = getAllIds(newTree)
				setExpandedNodes(newExpandedNodes)
				setTree(newTree)
			},
			[tree]
		)

		const handleDeleteItem = (nodeId: number) => {
			const newTree = removeNodeFromTree(tree, nodeId)
			setTree(newTree)
		}

		const handleExpansionItemsChange = (event, ids: Id[]) => {
			setExpandedNodes(ids)
		}

		const context = useMemo(
			() => ({
				name,
				open,
				onClose: () => setOpen(false),
				onSave: handleAddItems,
			}),
			[name, open, handleAddItems]
		)

		return (
			<Box
				ref={fieldRef}
				sx={{
					'@keyframes shake': errorShake,
					animation: error ? 'shake 0.25s' : null,
				}}
			>
				<Stack gap={1}>
					<Stack
						direction="row"
						sx={{ width: '100%' }}
						spacing={1}
						alignItems="center"
						justifyContent="space-between"
					>
						<Typography color={hasError ? 'error' : 'textPrimary'} variant="body1">
							{!required ? label : `${label} *`}
						</Typography>
						<Stack direction="row" spacing={1} alignItems="center">
							{rightSlot}
							<IconButton
								disabled={disabled}
								onClick={() => setOpen(true)}
								size="small"
								icon={faSearch}
							/>
						</Stack>
					</Stack>
					<RHFTreeInputProvider value={context}>
						<DialogComponent />
					</RHFTreeInputProvider>

					<Box
						ref={ref}
						sx={{
							minHeight: '6rem',
							maxHeight: '20rem',
							overflowY: 'auto',
							border: `${hasError ? `1px solid ${theme.palette.error.main}` : `1px solid ${theme.palette.grey[400]}`}`,
							borderRadius: '0.25rem',
							cursor: `${disabled || tree.length ? 'default' : 'pointer'}`,
							'&:hover': {
								borderColor: hasError ? theme.palette.error.main : theme.palette.text.primary,
							},
						}}
						onClick={disabled || tree.length ? null : () => setOpen(true)}
					>
						{!tree.length && (
							<Center sx={{ height: '6rem' }}>
								<Typography color="textSecondary" variant="body2">
									Search...
								</Typography>
							</Center>
						)}
						<RichTreeView
							onExpandedItemsChange={handleExpansionItemsChange}
							expandedItems={expandedNodes}
							items={tree}
							getItemLabel={(item) => item.name}
							slots={{ item: CustomTreeItem }}
							slotProps={{ item: { onDelete: (nodeId) => handleDeleteItem(nodeId) } as any }}
						/>
					</Box>
					{error && <Typography color="error.main">{error.message}</Typography>}
				</Stack>
			</Box>
		)
	}
)

RHFTreeInput.CustomerDialog = CustomerTreeInputDialog
RHFTreeInput.DistributorDialog = DistributorTreeInputDialog
RHFTreeInput.CategoryDialog = CategoryTreeInputDialog

export default RHFTreeInput
